import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector, useAppDispatch } from '../../hooks';
import { setVillageBack } from '../../slices/uiSlice';
import { cancelFromServer, fromServer, toServer } from '../../util/ServerSocket';
import { Walker, Model, loadPicture, loadVillageModel, walkerPosition, drawWalker } from './actors';
import { villageNameParts } from './nameplate';
import { hasExclusiveNeon } from '../Chat/exclusiveName';
import { drawTitleBadge } from '../../components/TitleBadge/TitleBadge';
import { NpcDialogue } from './NpcDialogue';
import { ProvinceWarRoom } from '../ProvinceWar/ProvinceWarRoom';
import { VillageNpc, VillageNpcSprites, VILLAGE_NPCS, NPC_STOP_Y, hasArrivedAtNpc } from './npcs';
import './VillageBack.css';

const villages: Record<number, string> = { 111: 'Leaf', 211: 'Mist', 311: 'Cloud', 411: 'Sand', 511: 'Rock' };
export function VillageBack() {
  const { t } = useTranslation();
  const scene = useAppSelector((s) => s.scene.scene),
    fight = useAppSelector((s) => s.fight.fight);
  const dispatch = useAppDispatch();
  const open = useAppSelector((s) => s.ui.villageBack);
  // Province-war rooms live inside the courtyard. Preserve them behind their battle
  // so playback completion can return both participants to the same ready room.
  const available = Object.keys(villages).includes(String(scene)) && (!fight || fight.provinceWar === true);
  useEffect(() => {
    if (!available) dispatch(setVillageBack(false));
  }, [available, dispatch]);
  useEffect(
    () => () => {
      dispatch(setVillageBack(false));
    },
    [scene, dispatch]
  );
  return (
    <>
      {available && !open && (
        <button
          className="vb-launcher"
          title={String(t('village_back_title'))}
          aria-label={String(t('village_back_title'))}
          onClick={() => dispatch(setVillageBack(true))}>
          <img src={`${process.env.PUBLIC_URL}/village-back/icon_konoha.png`} alt="" />
        </button>
      )}
      {open && available && <VillageBackScene />}
    </>
  );
}
export function VillageBackScene() {
  const { t } = useTranslation();
  const multiFight = useAppSelector(s => s.ui.multiFight);
  const [claimingChain, setClaimingChain] = useState(false);
  const retryEntry = useRef<() => void>(() => {});
  useEffect(() => {
    if (claimingChain && multiFight === null) {
      setClaimingChain(false);
      retryEntry.current();
    }
  }, [claimingChain, multiFight]);
  useEffect(() => {
    if (!claimingChain) return;
    const timer = window.setTimeout(() => setClaimingChain(false), 15000);
    return () => window.clearTimeout(timer);
  }, [claimingChain]);
  const canvas = useRef<HTMLCanvasElement>(null);
  const players = useRef(new Map<number, Walker>()),
    models = useRef(new Map<number, Model>()),
    pending = useRef(new Set<number>());
  const [ready, setReady] = useState(false),
    [count, setCount] = useState(0),
    [error, setError] = useState('');
  const self = useRef(0),
    clockOffset = useRef(0);
  const destination = useRef<{ x: number; y: number; at: number; duration: number } | null>(null);
  const npcVisit = useRef<{ npc: VillageNpc; accepted: boolean; expires: number } | null>(null);
  const [talkingNpc, setTalkingNpc] = useState<VillageNpc | null>(null);
  const [warRoom,setWarRoom]=useState<{selfId:number;village:number}|null>(null);

  function moveTo(targetX: number, targetY: number, npc?: VillageNpc) {
    if (!ready) return;
    setTalkingNpc(null);
    const own = players.current.get(self.current);
    const position = own ? walkerPosition(own, Date.now() - clockOffset.current) : { x: targetX, y: targetY };
    const duration = Math.max(900, (Math.hypot(position.x - targetX, position.y - targetY) / 145) * 1000 + 400);
    destination.current = { x: targetX, y: targetY, at: Date.now(), duration };
    const visit = npc ? { npc, accepted: false, expires: Date.now() + duration + 8000 } : null;
    npcVisit.current = visit;
    toServer('villageBack', { action: 'move', x: targetX, y: targetY }, (reply: any) => {
      if (npcVisit.current !== visit) return;
      if (reply?.ok === false) {
        npcVisit.current = null;
        setError(reply.error ?? 'village_back_error');
      } else if (visit && reply?.ok) visit.accepted = true;
    });
  }
  useEffect(() => {
    let active = true,
      animation = 0;
    const badges = new Map<number, HTMLImageElement>();
    let vipBadge: HTMLImageElement | undefined;
    loadPicture(`${process.env.REACT_APP_CDN_PATH ?? ''}ui/UIResource/Icon/vip4.gif`).then(image => { if (active) vipBadge = image; }).catch(() => {});
    Object.entries(villages).forEach(([id, name]) => {
      loadPicture(`${process.env.REACT_APP_CDN_PATH ?? ''}scenes/newPlayer/village${name}.png`)
        .then((image) => {
          if (active) badges.set(Number(id), image);
        })
        .catch(() => {});
    });
    const upsert = (player: Walker) => {
      players.current.set(player.id, player);
      setCount(players.current.size);
      if (!models.current.has(player.model) && !pending.current.has(player.model)) {
        pending.current.add(player.model);
        loadVillageModel(player.model)
          .then((model) => {
            if (active) models.current.set(player.model, model);
          })
          .catch(() => {
            if (active) setError('village_back_assets');
          })
          .finally(() => pending.current.delete(player.model));
      }
    };
    const update = (packet: any) => {
      if (!active) return;
      if (packet.type === 'remove') {
        players.current.delete(packet.id);
        setCount(players.current.size);
      } else if (packet.type === 'upsert') {
        upsert(packet.player);
      }
    };
    fromServer('villageBackState', update);
    const enter = () => toServer('villageBack', { action: 'enter' }, (reply: any) => {
      if (!active) {
        toServer('villageBack', { action: 'leave' });
        return;
      }
      if (!reply?.ok) {
        setError(reply?.error ?? 'village_back_error');
        return;
      }
      setError('');
      self.current = reply.data.self;
      clockOffset.current = Date.now() - reply.data.now;
      reply.data.players.forEach(upsert);
      setReady(true);
    });
    retryEntry.current = enter;
    enter();
    const context = canvas.current?.getContext('2d');
    const resize = () => {
      if (!canvas.current || !context) return;
      const bounds = canvas.current.getBoundingClientRect(),
        ratio = Math.min(window.devicePixelRatio || 1, 2);
      canvas.current.width = Math.max(1, Math.round(bounds.width * ratio));
      canvas.current.height = Math.max(1, Math.round(bounds.height * ratio));
      context.setTransform(canvas.current.width / 1000, 0, 0, canvas.current.height / 600, 0, 0);
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = 'high';
    };
    resize();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(resize) : null;
    if (canvas.current) observer?.observe(canvas.current);
    window.addEventListener('resize', resize);
    const rendered = new Map<number, { x: number; y: number }>();
    let previousFrame = performance.now();
    const draw = () => {
      if (!active || !context) return;
      context.clearRect(0, 0, 1000, 600);
      const now = Date.now() - clockOffset.current;
      for (const id of Array.from(rendered.keys())) if (!players.current.has(id)) rendered.delete(id);
      const frameNow = performance.now(),
        delta = Math.min(100, frameNow - previousFrame);
      previousFrame = frameNow;
      const marker = destination.current;
      if (marker) {
        const elapsed = Date.now() - marker.at;
        if (elapsed > marker.duration) destination.current = null;
        else {
          const pulse = (elapsed % 750) / 750,
            fade = Math.min(1, (marker.duration - elapsed) / 250);
          context.save();
          context.translate(marker.x, marker.y);
          context.globalAlpha = fade;
          context.shadowColor = '#ffe57e';
          context.shadowBlur = 8;
          context.strokeStyle = '#fff2a8';
          context.lineWidth = 2;
          context.beginPath();
          context.ellipse(0, 0, 12 + pulse * 16, 4 + pulse * 5, 0, 0, Math.PI * 2);
          context.stroke();
          context.fillStyle = '#ffe28a';
          context.beginPath();
          context.ellipse(0, 0, 4, 2, 0, 0, Math.PI * 2);
          context.fill();
          context.restore();
        }
      }
      const ordered = Array.from(players.current.values()).sort((a, b) => walkerPosition(a, now).y - walkerPosition(b, now).y);
      for (const player of ordered) {
        const desired = walkerPosition(player, now),
          model = models.current.get(player.model);
        const last = rendered.get(player.id) ?? desired,
          blend = 1 - Math.exp(-delta / 35);
        const position = { x: last.x + (desired.x - last.x) * blend, y: last.y + (desired.y - last.y) * blend };
        if (Math.hypot(position.x - desired.x, position.y - desired.y) < 0.05) {
          position.x = desired.x;
          position.y = desired.y;
        }
        rendered.set(player.id, position);
        const visit = npcVisit.current;
        if (visit && player.id === self.current) {
          if (Date.now() > visit.expires) npcVisit.current = null;
          else if (visit.accepted && hasArrivedAtNpc(player, visit.npc, now, position)) {
            npcVisit.current = null;
            destination.current = null;
            setTalkingNpc(visit.npc);
          }
        }
        if (model)
          drawWalker(context, player, model, now, {
            ...position,
            moving: desired.moving || Math.hypot(position.x - desired.x, position.y - desired.y) > 0.3,
          });
        context.save();
        context.font = '13px Tahoma,Arial';
        context.textBaseline = 'middle';
        context.lineJoin = 'round';
        context.lineWidth = 2.5;
        const parts = villageNameParts({...player,titleId:0}, key => String(t(key)));
        const badge = badges.get(player.village);
        const vip = (player.vipExpiresAt ?? 0) > Date.now() && !!vipBadge;
        const villageWidth = badge ? 21 : 0, vipWidth = vip ? 27 : 0;
        const width = parts.reduce((sum, part) => sum + context.measureText(part.text).width, 0) + villageWidth + vipWidth;
        const x = Math.max(12, Math.min(988 - width, position.x - width / 2)), y = position.y - 106;
        context.shadowColor = '#000000';
        context.shadowBlur = 3;
        context.shadowOffsetY = 1;
        context.strokeStyle = '#050505';
        let textX = x;
        const placed = parts.map((part, index) => {
          const placed = { ...part, x: textX };
          textX += context.measureText(part.text).width + (index === 0 ? villageWidth : 0);
          return placed;
        });
        // Outline the whole line first so the next letter cannot cover the previous letter's color.
        for (const part of placed) context.strokeText(part.text, part.x, y);
        const neonName = hasExclusiveNeon(player.name, player.nameColors, player.nameNeon);
        const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        for (let index = 0; index < placed.length; index++) {
          const part = placed[index];
          context.shadowColor = neonName && index > 0 && index < placed.length - 1 ? part.color : '#000000';
          context.shadowBlur = neonName && index > 0 && index < placed.length - 1 ? 7 + (reducedMotion ? 0 : 3 * Math.sin(now / 550 + index)) : 3;
          context.fillStyle = part.color;
          context.fillText(part.text, part.x, y);
          if (neonName && !reducedMotion && index > 0 && index < placed.length - 1) {
            const phase = (now / 2600 + index * 0.17) % 1;
            if (phase < 0.45) {
              context.save(); context.globalAlpha = Math.sin(phase / 0.45 * Math.PI);
              const sx = part.x + 4, sy = y - 7 - phase * 12;
              context.strokeStyle = part.color; context.lineWidth = 1;
              context.beginPath(); context.moveTo(sx - 2, sy); context.lineTo(sx + 2, sy);
              context.moveTo(sx, sy - 3); context.lineTo(sx, sy + 3); context.stroke(); context.restore();
            }
          }
        }
        context.shadowBlur = 0;
        if (badge) context.drawImage(badge, x + context.measureText(parts[0].text).width, y - 9, 18, 18);
        if (vip && vipBadge) context.drawImage(vipBadge, textX + 3, y - 9, 24, 18);
        context.restore();
        drawTitleBadge(context,player.titleId??0,position.x,position.y-146,now);
      }
      animation = requestAnimationFrame(draw);
    };
    animation = requestAnimationFrame(draw);
    return () => {
      active = false;
      cancelAnimationFrame(animation);
      observer?.disconnect();
      window.removeEventListener('resize', resize);
      cancelFromServer('villageBackState', update);
      toServer('villageBack', { action: 'leave' });
      players.current.clear();
      npcVisit.current = null;
    };
  }, []);
  return (
    <section className="vb-scene" aria-label={String(t('village_back_title'))}>
      <img className="vb-background" src={`${process.env.PUBLIC_URL}/village-back/background.png`} alt="" />
      <VillageNpcSprites />
      <canvas
        ref={canvas}
        width={1000}
        height={600}
        aria-label={String(t('village_back_walk'))}
        onPointerDown={(event) => {
          if (!ready || event.button !== 0) return;
          const rect = event.currentTarget.getBoundingClientRect(),
            x = ((event.clientX - rect.left) / rect.width) * 1000,
            y = ((event.clientY - rect.top) / rect.height) * 600;
          if (y < 535) return;
          moveTo(Math.max(40, Math.min(960, x)), Math.max(545, Math.min(563, y)));
        }}
      />
      {VILLAGE_NPCS.map(npc => <button key={npc.id} className="vb-npc-target" aria-label={`คุยกับ ${npc.name}`} title={npc.name} disabled={!ready}
        style={{ left: `${(npc.x - npc.width / 2) / 10}%`, top: `${(npc.y - npc.height) / 6}%`, width: `${npc.width / 10}%`, height: `${npc.height / 6}%` }}
        onClick={() => moveTo(npc.stopX, NPC_STOP_Y, npc)} />)}
      {talkingNpc && <NpcDialogue key={talkingNpc.id} npc={talkingNpc} onClose={()=>setTalkingNpc(null)} onEnter={room=>{setWarRoom(room);setTalkingNpc(null);}}/>}
      {warRoom&&<ProvinceWarRoom selfId={warRoom.selfId} selfVillage={warRoom.village} onClose={()=>setWarRoom(null)}/>}
      <header className="vb-header">
        <div>
          <b>{t('village_back_title')}</b>
          <small>{t('village_back_online', { count })}</small>
        </div>
      </header>
      {!ready && <p className="vb-hint">{t('village_back_connecting')}</p>}
      {error && (
        <div className="vb-error" role="alert">
          {multiFight !== null && multiFight <= Date.now() ? <>
            <p>ยังไม่ได้รับผลต่อสู้อัตโนมัติรอบที่จบแล้ว กรุณารับผลก่อนเข้าฉาก</p>
            <button type="button" disabled={claimingChain} onClick={() => { setClaimingChain(true); toServer('fieldFightMultiClaim'); }}>
              {claimingChain ? 'กำลังรับผล…' : 'รับผลต่อสู้อัตโนมัติแล้วลองเข้าใหม่'}
            </button>
          </> : t(error)}
        </div>
      )}
    </section>
  );
}
